'use client'

import { matchesRfqStreamAccount, rfqStreamPayloadDeps } from '@/service/ws/rfq-stream-scope'
import { createStreamHook } from '@/service/ws/use-web-socket-with-proto'
import { AccountDataStream } from '@/types/generated/public_api'
import { RfqAccept, RfqUpdateList } from '@/types/generated/rfq'

/**
 * Subscribes to the account RFQ channel (ACCOUNT_DATA_STREAM_TYPE_RFQS) and surfaces
 * each `DataPayload.rfqUpdates` frame. Each frame is a full snapshot of one RFQ **on one
 * network** — upsert by `(rfqId, rfqNetwork)` (see `upsertRfqs`), never by `rfqId` alone,
 * or a dual-network RFQ loses whichever leg's frame arrives first. Nothing folds these
 * frames server-side: unlike `get_rfq_history`, the live stream emits raw per-network
 * states, so the cross-network fold is the client's job (see `buildRfqCards`). No
 * snapshot on subscribe; seed from `get_rfq_history`.
 */
export const useRfqStream = createStreamHook<AccountDataStream, RfqUpdateList>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    matchesRfqStreamAccount(message.data?.accountId, payload)
      ? message.data?.rfqUpdates
      : undefined,
  getPayloadDeps: rfqStreamPayloadDeps,
  listenerChannelKey: 'accountStream',
})

/**
 * The second frame type on the RFQ channel: a venue-confirmed acceptance, emitted just
 * before the FILLED RfqUpdate for both manual and auto accepts. Optional to consume —
 * the FILLED RfqUpdate still drives state — but it names the exact quote/price crossed,
 * which powers an instant "accepted" affirmation.
 */
export const useRfqAcceptStream = createStreamHook<AccountDataStream, RfqAccept>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    matchesRfqStreamAccount(message.data?.accountId, payload) ? message.data?.rfqAccept : undefined,
  getPayloadDeps: rfqStreamPayloadDeps,
  listenerChannelKey: 'accountStream',
})
